import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import fnc from '../../mymod/commFunction';
import './SmsTemplateModal.scss';

function apiUrl(path) {
    const base = String(fnc.svrUrl || '').replace(/\/$/, '');
    const normalizedPath = String(path || '').replace(/^\//, '');
    return base ? `${base}/${normalizedPath}` : `/${normalizedPath}`;
}

const emptyEditor = { id: 0, title: '', content: '' };

const SmsTemplateModal = ({ open, onClose, onSelect }) => {
    const [templates, setTemplates] = useState([]);
    const [editor, setEditor] = useState(emptyEditor);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const isOverSmsLimit = new TextEncoder().encode(editor.content || '').length > 90;

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(apiUrl('/smsRoutes/templates'), {});
            const payload = response.data || {};
            if (!payload.success) throw new Error(payload.message || '상용구 조회 실패');
            setTemplates(Array.isArray(payload.rows) ? payload.rows : []);
        } catch (loadError) {
            setError(loadError.response?.data?.message || loadError.message || '상용구 조회에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        setEditor(emptyEditor);
        setSelectedTemplate(null);
        loadTemplates();
    }, [open, loadTemplates]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const resetEditor = () => {
        setEditor(emptyEditor);
        setSelectedTemplate(null);
        setError('');
    };

    const saveTemplate = async () => {
        const title = editor.title.trim();
        const content = editor.content.trim();
        if (!title || !content) {
            setError('제목과 내용을 입력하세요.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const response = await axios.post(apiUrl('/smsRoutes/templates/save'), {
                title,
                content
            });
            if (!response.data?.success) throw new Error(response.data?.message || '상용구 저장 실패');
            await loadTemplates();
        } catch (saveError) {
            setError(saveError.response?.data?.message || saveError.message || '상용구 저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const deleteTemplate = async (template = editor) => {
        const targetId = Number(template?.id) || 0;
        const targetTitle = String(template?.title || '');
        if (!targetId) return;
        if (!window.confirm(`'${targetTitle}' 상용구를 삭제할까요?`)) return;

        setSaving(true);
        setError('');
        try {
            const response = await axios.post(apiUrl('/smsRoutes/templates/delete'), { id: targetId });
            if (!response.data?.success) throw new Error(response.data?.message || '상용구 삭제 실패');
            if (Number(editor.id) === targetId) setEditor(emptyEditor);
            if (Number(selectedTemplate?.id) === targetId) setSelectedTemplate(null);
            await loadTemplates();
        } catch (deleteError) {
            setError(deleteError.response?.data?.message || deleteError.message || '상용구 삭제에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const selectTemplate = (template) => {
        setSelectedTemplate(template);
        setEditor({
            id: Number(template.id) || 0,
            title: template.title || '',
            content: template.content || ''
        });
    };

    const applySelectedTemplate = () => {
        if (!selectedTemplate) return;
        onSelect(editor.content || selectedTemplate.content || '');
        onClose();
    };

    return (
        <div className="sms-template-backdrop" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
        }}>
            <section className="sms-template-modal" role="dialog" aria-modal="true" aria-labelledby="sms-template-title">
                <header className="sms-template-header">
                    <div>
                        <h2 id="sms-template-title">상용구</h2>
                    </div>
                    <button type="button" className="sms-template-close" onClick={onClose} aria-label="닫기">×</button>
                </header>

                <div className="sms-template-body">
                    <div className="sms-template-list-panel">
                        <div className="sms-template-grid-wrap">
                            <table className="sms-template-grid">
                                <colgroup>
                                    <col className="template-no-column" />
                                    <col className="template-title-column" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>no</th>
                                        <th>제목</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td className="sms-template-empty" colSpan="2">조회 중...</td></tr>
                                    ) : templates.length === 0 ? (
                                        <tr><td className="sms-template-empty" colSpan="2">저장된 상용구가 없습니다.</td></tr>
                                    ) : templates.map((template, index) => (
                                        <tr
                                            key={template.id}
                                            className={Number(selectedTemplate?.id) === Number(template.id) ? 'is-selected' : ''}
                                            onClick={() => selectTemplate(template)}
                                        >
                                            <td className="template-no-cell">{index + 1}</td>
                                            <td className="template-title-cell" title={template.title}>
                                                <span>{template.title}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="sms-template-apply-bar">
                            <button type="button" onClick={applySelectedTemplate} disabled={!selectedTemplate}>선택</button>
                        </div>
                    </div>

                    <div className="sms-template-editor-panel">
                        <label>
                            <input
                                aria-label="상용구 제목"
                                value={editor.title}
                                maxLength={100}
                                onChange={(event) => setEditor((prev) => ({ ...prev, title: event.target.value }))}
                                placeholder="상용구 제목"
                            />
                        </label>
                        <label className="sms-template-content-field">
                            <textarea
                                aria-label="상용구 내용"
                                className={isOverSmsLimit ? 'is-over-limit' : ''}
                                value={editor.content}
                                onChange={(event) => setEditor((prev) => ({ ...prev, content: event.target.value }))}
                                placeholder="전송할 메시지 내용을 입력하세요."
                            />
                        </label>
                        {error && <div className="sms-template-error">{error}</div>}
                        <div className="sms-template-actions">
                            <button type="button" className="sms-template-reset" onClick={resetEditor} disabled={saving}>초기화</button>
                            <button type="button" className="sms-template-save" onClick={saveTemplate} disabled={saving}>
                                {saving ? '처리 중...' : '저장'}
                            </button>
                            <button
                                type="button"
                                className="sms-template-delete"
                                onClick={() => deleteTemplate(selectedTemplate)}
                                disabled={saving || !selectedTemplate}
                            >
                                삭제
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SmsTemplateModal;
